import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./rules.css";
import { s3PublicUrl } from "../../utils/s3PublicUrl";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchGameStages } from "../../functions/stageReport";
import { fetchReport } from "../../functions/fetchReport";
import { mergeGameAssignmentIntoUserData } from "../../utils/gameStages";
import { getStagePath } from "../../utils/stageRoutes";

const MAX_RULES = 6;

const RULE_ICON_POOL = [
  "fa-brain",
  "fa-puzzle-piece",
  "fa-trophy",
  "fa-star",
  "fa-rocket",
  "fa-bullseye",
  "fa-bolt",
  "fa-heart",
  "fa-flag",
  "fa-gem",
  "fa-fire",
  "fa-medal",
  "fa-lightbulb",
  "fa-gamepad",
  "fa-users",
  "fa-wand-magic-sparkles",
];

const RULE_ICON_COLORS = [
  "#f472b6",
  "#fb923c",
  "#a78bfa",
  "#fbbf24",
  "#60a5fa",
  "#34d399",
  "#f87171",
  "#818cf8",
];

function pickRandomRuleIcons(count) {
  const pool = [...RULE_ICON_POOL];
  const icons = [];
  for (let i = 0; i < count; i += 1) {
    if (pool.length === 0) pool.push(...RULE_ICON_POOL);
    const idx = Math.floor(Math.random() * pool.length);
    icons.push({
      icon: pool.splice(idx, 1)[0],
      color: RULE_ICON_COLORS[Math.floor(Math.random() * RULE_ICON_COLORS.length)],
    });
  }
  return icons;
}

function rulesThemeVisuals(themeData) {
  const t = themeData || {};
  const textColor =
    (t.text_color != null && String(t.text_color).trim()) || "#1e2a4a";
  const buttonTextColor =
    (t.button_text_color != null && String(t.button_text_color).trim()) ||
    (t.box_text_color != null && String(t.box_text_color).trim()) ||
    "#ffffff";
  const buttonBgColor =
    (t.button_background_color != null && String(t.button_background_color).trim()) ||
    (t.box_bg_color != null && String(t.box_bg_color).trim()) ||
    "#6845d3";

  return { textColor, buttonTextColor, buttonBgColor };
}

/** Darken a #RRGGBB color for gradient stops. */
function darkenHex(hex, factor = 0.72) {
  const raw = String(hex ?? "").trim().replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return "#4c2eb8";
  const mix = (n) =>
    Math.max(0, Math.min(255, Math.round(parseInt(n, 16) * factor)));
  return `#${mix(raw.slice(0, 2)).toString(16).padStart(2, "0")}${mix(raw.slice(2, 4))
    .toString(16)
    .padStart(2, "0")}${mix(raw.slice(4, 6)).toString(16).padStart(2, "0")}`;
}

function RuleIcon({ icon, color }) {
  return (
    <span className="htp-step-icon htp-step-icon--fa" style={{ color }} aria-hidden>
      <i className={`fa-solid ${icon}`} />
    </span>
  );
}

function DecorPiece({ className, color }) {
  return (
    <span className={`htp-decor-piece ${className}`} aria-hidden>
      <svg viewBox="0 0 48 48">
        <path
          d="M4 4h14v6c0 3 3 3 6 0V4h14v14h-6c-3 0-3 3 0 6h6v14H24v-6c0-3-3-3-6 0v6H4V24h6c3 0 3-3 0-6H4V4Z"
          fill={color}
        />
      </svg>
    </span>
  );
}

const UserRules = () => {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const authUser = useSelector((state) => state.auth?.user);
  const { data: themeData, status: themeStatus } = useSelector((state) => state.theme);
  const [continuing, setContinuing] = useState(false);

  const rules = useMemo(() => {
    if (!themeData || !Array.isArray(themeData.rules)) return [];
    return themeData.rules
      .map((r) => String(r ?? "").trim())
      .filter(Boolean)
      .slice(0, MAX_RULES);
  }, [themeData]);

  const ruleCards = useMemo(() => {
    const icons = pickRandomRuleIcons(rules.length);
    return rules.map((description, index) => ({
      number: index + 1,
      description,
      icon: icons[index]?.icon ?? RULE_ICON_POOL[0],
      iconColor: icons[index]?.color ?? RULE_ICON_COLORS[0],
    }));
  }, [rules]);

  const visuals = useMemo(() => rulesThemeVisuals(themeData), [themeData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const userId = authUser?.userId;
    const organizationId = authUser?.organizationId;
    const sessionId = authUser?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const resp = await fetchReport({ userId, token, sessionId, organizationId });
        if (cancelled) return;
        const reportStatus = String(resp?.report?.status ?? "").trim().toLowerCase();
        if (reportStatus === "completed") {
          navigate("/big-picture", { replace: true });
        }
      } catch {
        /* allow rules page when report fetch fails */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminToken, authUser, navigate]);

  const soundEnabled =
    themeData?.sound_toggle === true ||
    themeData?.sound_toogle === true ||
    String(themeData?.sound_toggle ?? themeData?.sound_toogle).toLowerCase() === "true";

  const playContinueClick = () => {
    if (!soundEnabled) return;
    const raw = themeData?.default_question_sound;
    if (raw == null || String(raw).trim() === "") return;
    const url = s3PublicUrl(raw);
    if (!url || typeof Audio === "undefined") return;
    try {
      const a = new Audio(url);
      a.volume = 0.92;
      void a.play().catch(() => {});
    } catch {
      /* ignore */
    }
  };

  const onContinue = async () => {
    if (continuing) return;
    playContinueClick();

    const userId = authUser?.userId;
    const organizationId = authUser?.organizationId;
    const sessionId = authUser?.sessionId;

    setContinuing(true);
    try {
      const data = await fetchGameStages({ token: adminToken, userId, organizationId, sessionId });
      mergeGameAssignmentIntoUserData({
        game: data?.game ?? null,
        currentStage: data?.currentStage ?? null,
        nextDay: data?.nextDay ?? null,
      });

      const currentStage = data?.currentStage != null ? Number(data.currentStage) : null;
      const nextDay = data?.nextDay != null ? Number(data.nextDay) : null;

      if (!Number.isFinite(currentStage) || currentStage < 1) {
        navigate("/puzzle", { replace: true });
        return;
      }

      const shouldAutoPlay =
        nextDay != null &&
        Number.isFinite(nextDay);

      navigate(getStagePath(data?.currentStage) || '/puzzle', {
        state: {
          game: data?.game ?? null,
          currentStage,
          nextDay,
          autoPlay: shouldAutoPlay,
        },
      });
    } catch (e) {
      console.warn("fetchGameStages failed:", e?.message || e);
      Swal.fire("Error", "Failed to fetch game stages", "error");
    } finally {
      setContinuing(false);
    }
  };

  const pageStyle = {
    "--htp-text": visuals.textColor,
    "--htp-btn-bg": visuals.buttonBgColor,
    "--htp-btn-bg-end": darkenHex(visuals.buttonBgColor),
    "--htp-btn-text": visuals.buttonTextColor,
    "--htp-accent": visuals.buttonBgColor,
    "--htp-card-accent": visuals.buttonBgColor,
  };

  if (themeStatus === "loading" || themeStatus === "idle") {
    return (
      <div className="htp-page" style={pageStyle}>
        <div className="htp-loader-wrap" aria-busy="true" aria-label="Loading rules">
          <div className="htp-loader" />
        </div>
      </div>
    );
  }

  return (
    <div className="htp-page" style={pageStyle}>
      <DecorPiece className="htp-decor-piece--tl" color="#93c5fd" />
      <DecorPiece className="htp-decor-piece--tr" color="#f9a8d4" />
      <DecorPiece className="htp-decor-piece--bl" color="#c4b5fd" />
      <DecorPiece className="htp-decor-piece--br" color="#fde047" />
      <DecorPiece className="htp-decor-piece--mid" color="#a5b4fc" />

      <div className="htp-inner">
        <div className="htp-hero">
          <h1 className="htp-title">HOW TO PLAY ?</h1>
          <p className="htp-subtitle">Your journey to the Big Picture</p>
        </div>

        <div className="htp-main">
          {ruleCards.length > 0 ? (
            <div
              className="htp-cards"
              style={{ "--htp-rule-count": ruleCards.length }}
            >
              {ruleCards.map((rule) => (
                <article className="htp-card" key={`rule-${rule.number}`}>
                  <RuleIcon icon={rule.icon} color={rule.iconColor} />
                  <h2 className="htp-card-title">
                    <span className="htp-card-num">{rule.number}</span>
                  </h2>
                  <p className="htp-card-desc">{rule.description}</p>
                </article>
              ))}
            </div>
          ) : (
            <p className="htp-rules-empty">No rules available at this time.</p>
          )}
        </div>

        <div className="htp-actions">
          <button
            type="button"
            className="htp-go-btn"
            onClick={onContinue}
            disabled={continuing}
            aria-busy={continuing}
          >
            {continuing ? "Loading…" : "LET'S GO!"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserRules;
